import { useEffect, useId, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

/**
 * One closed-by-default group on the account page (D-719).
 *
 * `asked`: *"cleanup the giant list of your account now for teachers, we need to
 * group like stuff together in sub sections that maybe come pre collapsed so
 * they aren't having to scroll a ton"*. A teacher who is also a parent had
 * twelve boxes in one column.
 *
 * **The summary is what makes closed acceptable** - it says what is set, so the
 * page answers "am I set up?" without opening anything.
 *
 * **An address opens it** (`/account#sign-in`), because the welcome dialog sends
 * people here to set up notifications and a passkey, and landing on a page of
 * closed groups would be the link that arrives somewhere unable to answer.
 *
 * **Open or closed is remembered per browser** - a convenience, so it lives in
 * `localStorage` and every read and write survives that being unavailable.
 */
export default function AccountGroup({
  id,
  title,
  summary,
  children,
}: {
  id: string
  title: string
  summary?: string
  children: ReactNode
}) {
  const { hash } = useLocation()
  const bodyId = useId()
  const storageKey = `account.group.${id}`

  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) === 'open'
    } catch {
      return false
    }
  })

  useEffect(() => {
    if (hash === `#${id}`) setOpen(true)
  }, [hash, id])

  const toggle = () => {
    const next = !open
    setOpen(next)
    try {
      window.localStorage.setItem(storageKey, next ? 'open' : 'closed')
    } catch {
      // Private window or blocked storage: it simply is not remembered.
    }
  }

  return (
    <section id={id} className="mt-3 scroll-mt-4">
      <h2>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-[56px] w-full items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-left hover:border-line-2"
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-[14.5px] font-semibold text-ink">{title}</span>
            {summary && <span className="text-[12.5px] wrap-anywhere text-ink-3">{summary}</span>}
          </span>
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={`shrink-0 text-ink-3 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </h2>

      {/*
        Mounted while closed, only hidden: the panels load what they did before
        this grouping existed, and a half-typed field survives closing the group.
        `[&>*]:mt-0` because three panels carried their own top margin for the
        single column they used to sit in.
      */}
      <div id={bodyId} hidden={!open} className="mt-2 flex flex-col gap-3 [&>*]:mt-0">
        {children}
      </div>
    </section>
  )
}
